// Identita.tsx — nel profilo: con che cosa si entra (Google, Apple) e il collegamento della seconda identità.
// Collegare richiede la sessione aperta (è la conferma di chi usa l'account) e un accesso nuovo al fornitore.
// Scollegare: mai l'ultima (il server lo rifiuta comunque).
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { collegaIdentita, nuovoNonce, scollegaIdentita, type Io } from "../servizi/account.ts";
import { accediConApple, disponibileSuQuestaPiattaforma, pulsanteGoogle } from "../servizi/accessoWeb.ts";
import { ErroreRete } from "../servizi/api.ts";
import { eTesto, leggiImpostazione } from "../servizi/impostazioni.ts";
import { IMP_APP } from "../costanti.ts";

const NOMI = { google: "Google", apple: "Apple" } as const;
const ERRORI = ["identita_in_uso", "fornitore_gia_collegato", "ultima_identita", "token_non_valido", "offline", "timeout"];
const codice = (e: unknown) => {
  let c = e instanceof ErroreRete ? e.codice : "generico";
  if (c === "fornitore_non_raggiungibile" || c === "servizio_non_disponibile") c = "timeout";
  return ERRORI.includes(c) ? c : "generico";
};

export function Identita({ io }: { io: Io }) {
  const { t, i18n } = useTranslation();
  const [errore, setErrore] = useState<string | null>(null);
  const [fatto, setFatto] = useState<string | null>(null);
  const [nonce, setNonce] = useState<string | null>(null);
  const [giro, setGiro] = useState(0);
  const [invio, setInvio] = useState(false);
  const google = useRef<HTMLDivElement>(null);
  const clientGoogle = leggiImpostazione(IMP_APP.googleClientIdWeb, "", eTesto);
  const serviceApple = leggiImpostazione(IMP_APP.appleServiceId, "", eTesto);
  const web = disponibileSuQuestaPiattaforma();
  const ha = (f: "google" | "apple") => io.identita.some((i) => i.fornitore === f);
  const manca = ([["google", clientGoogle], ["apple", serviceApple]] as const).filter(([f, conf]) => !ha(f) && conf).map(([f]) => f);

  // Nonce dal server (uno per tentativo), solo se c'è qualcosa da collegare.
  useEffect(() => {
    if (!web || manca.length === 0) return;
    let vivo = true;
    nuovoNonce()
      .then((n) => vivo && setNonce(n))
      .catch((e: unknown) => vivo && setErrore(codice(e)));
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [web, manca.length, giro]);

  const collega = (f: "google" | "apple", n: string) => (token: string) => {
    setInvio(true);
    setErrore(null);
    collegaIdentita(f, token, n)
      .then(() => setFatto(f))
      .catch((e: unknown) => setErrore(codice(e)))
      .finally(() => {
        setInvio(false);
        setGiro((g) => g + 1);
      });
  };

  useEffect(() => {
    if (!web || !nonce || !google.current || !manca.includes("google")) return;
    pulsanteGoogle(google.current, clientGoogle, nonce, i18n.language, collega("google", nonce)).catch(() => setErrore("generico"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [web, nonce, clientGoogle, i18n.language, manca.includes("google")]);

  return (
    <div className="identita" data-identita={io.identita.map((i) => i.fornitore).join(",")}>
      <ul className="elenco-identita">
        {io.identita.map((i) => (
          <li key={i.fornitore}>
            <span>
              {NOMI[i.fornitore]}
              {i.email && <span className="nota"> · {i.email}</span>}
            </span>
            {io.identita.length > 1 && (
              <button
                type="button"
                className="pulsante-link"
                disabled={invio}
                onClick={() => {
                  setErrore(null);
                  setFatto(null);
                  scollegaIdentita(i.fornitore).catch((e: unknown) => setErrore(codice(e)));
                }}
              >
                {t("account.scollega", { fornitore: NOMI[i.fornitore] })}
              </button>
            )}
          </li>
        ))}
      </ul>
      {web && manca.length > 0 && (
        <div className="collega-identita" aria-busy={invio}>
          <p className="nota">{t("account.collega_testo")}</p>
          {manca.includes("google") && <div ref={google} className="pulsante-google" />}
          {manca.includes("apple") && (
            <button
              type="button"
              className="pulsante pulsante-secondario pulsante-piccolo"
              disabled={invio || !nonce}
              onClick={() => {
                if (!nonce) return;
                const n = nonce;
                setErrore(null);
                accediConApple(serviceApple, n)
                  .then((tok) => tok && collega("apple", n)(tok))
                  .catch(() => setErrore("generico"));
              }}
            >
              {t("account.collega", { fornitore: "Apple" })}
            </button>
          )}
        </div>
      )}
      {fatto && (
        <p className="nota" role="status">
          {t("account.collegata", { fornitore: NOMI[fatto as "google" | "apple"] })}
        </p>
      )}
      {errore && (
        <p className="admin-errore" role="alert">
          {t(`account.errore_${errore}`, { defaultValue: t("account.errore_generico") })}
        </p>
      )}
    </div>
  );
}
